import { gql, useLazyQuery, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { ChevronDownIcon, ChevronUpIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import logger from '../../lib/logger';
import { formatTimeAgo } from '../../utils/formatting';

import AdminSelectUser from './AdminSelectUser';

import type {
  AdminGetUserBankConnectionsQuery,
  AdminGetUserBankConnectionsQueryVariables,
  AdminReSyncFinancialDataMutation,
  AdminReSyncFinancialDataMutationVariables,
  AdminDeleteBankConnectionMutation,
  AdminDeleteBankConnectionMutationVariables,
} from './__generated__/AdminBankConnectionsPane.gql';

const ADMIN_GET_USER_BANK_CONNECTIONS = gql`
  query AdminGetUserBankConnections($userId: String!) {
    adminGetUserBankConnections(userId: $userId) {
      id
      externalId
      lastSyncedAt
      bank {
        id
        name
        logoUrl
      }
      user {
        id
        email
      }
      accounts {
        id
        name
        mask
        type
        subtype
      }
    }
    adminGetBankConnectionsHealth(userId: $userId) {
      bankConnectionId
      stale
      neverSynced
      plaidJSON
      error
    }
  }
`;

const ADMIN_RESYNC_FINANCIAL_DATA = gql`
  mutation AdminReSyncFinancialData($bankConnectionId: ID!, $userId: ID!, $resetCursor: Boolean) {
    adminReSyncFinancialData(bankConnectionId: $bankConnectionId, userId: $userId, resetCursor: $resetCursor) {
      id
      lastSyncedAt
    }
  }
`;

const ADMIN_DELETE_BANK_CONNECTION = gql`
  mutation AdminDeleteBankConnection($bankConnectionId: ID!, $userId: ID!) {
    adminDeleteBankConnection(bankConnectionId: $bankConnectionId, userId: $userId)
  }
`;

const AdminBankConnectionsPane = () => {
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [bankConnectionToDelete, setBankConnectionToDelete] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = React.useState<Set<string>>(new Set());
  const [expandedHealthJSON, setExpandedHealthJSON] = React.useState<Set<string>>(new Set());

  const [
    loadBankConnections,
    { data: bankConnectionsData, loading: bankConnectionsLoading, refetch: refetchConnections },
  ] = useLazyQuery<AdminGetUserBankConnectionsQuery, AdminGetUserBankConnectionsQueryVariables>(
    ADMIN_GET_USER_BANK_CONNECTIONS
  );

  const [resyncFinancialData] = useMutation<
    AdminReSyncFinancialDataMutation,
    AdminReSyncFinancialDataMutationVariables
  >(ADMIN_RESYNC_FINANCIAL_DATA);

  const [deleteBankConnection] = useMutation<
    AdminDeleteBankConnectionMutation,
    AdminDeleteBankConnectionMutationVariables
  >(ADMIN_DELETE_BANK_CONNECTION);

  // Load bank connections when user is selected
  React.useEffect(() => {
    if (selectedUserId) {
      void loadBankConnections({ variables: { userId: selectedUserId } });
    }
  }, [selectedUserId, loadBankConnections]);

  // Create a map of health status by bank connection ID for easy lookup
  const healthStatusMap = React.useMemo(() => {
    const map = new Map<
      string,
      NonNullable<AdminGetUserBankConnectionsQuery['adminGetBankConnectionsHealth']>[number]
    >();
    bankConnectionsData?.adminGetBankConnectionsHealth?.forEach((status) => {
      map.set(status.bankConnectionId, status);
    });
    return map;
  }, [bankConnectionsData]);

  const handleUserSelect = (userId: string | null) => {
    setSelectedUserId(userId);
    // Close delete modal and reset bank connection to delete when user changes
    setDeleteModalOpen(false);
    setBankConnectionToDelete(null);
    setExpandedHealthJSON(new Set());
  };

  const toggleHealthJSONExpanded = (bankConnectionId: string) => {
    setExpandedHealthJSON((prev) => {
      const next = new Set(prev);
      if (next.has(bankConnectionId)) {
        next.delete(bankConnectionId);
      } else {
        next.add(bankConnectionId);
      }
      return next;
    });
  };

  const handleResyncFinancialData = async (bankConnectionId: string) => {
    if (!selectedUserId) {
      alert('Please select a user first.');
      return;
    }
    setActionLoading(bankConnectionId);
    try {
      await resyncFinancialData({ variables: { bankConnectionId, userId: selectedUserId } });
      await refetchConnections();
    } catch (error) {
      logger.error('Failed to resync financial data:', error);
      alert('Failed to resync financial data. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResyncWithResetCursor = async (bankConnectionId: string) => {
    if (!selectedUserId) {
      alert('Please select a user first.');
      return;
    }
    setActionLoading(bankConnectionId);
    try {
      await resyncFinancialData({
        variables: { bankConnectionId, userId: selectedUserId, resetCursor: true },
      });
      await refetchConnections();
    } catch (error) {
      logger.error('Failed to resync financial data with reset cursor:', error);
      alert('Failed to resync financial data with reset cursor. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (bankConnectionId: string) => {
    setBankConnectionToDelete(bankConnectionId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bankConnectionToDelete || !selectedUserId) {
      return;
    }

    setActionLoading(bankConnectionToDelete);
    try {
      await deleteBankConnection({
        variables: { bankConnectionId: bankConnectionToDelete, userId: selectedUserId },
      });
      setDeleteModalOpen(false);
      setBankConnectionToDelete(null);
      await refetchConnections();
    } catch (error) {
      logger.error('Failed to delete bank connection:', error);
      alert('Failed to delete bank connection. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) {
      return 'Never';
    }
    return new Date(date).toLocaleString();
  };

  const toggleAccountsExpanded = (bankConnectionId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(bankConnectionId)) {
        next.delete(bankConnectionId);
      } else {
        next.add(bankConnectionId);
      }
      return next;
    });
  };

  const selectedUserEmail = bankConnectionsData?.adminGetUserBankConnections?.[0]?.user?.email || 'Selected User';

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Bank connection management</div>
        <div className="text-secondary text-sm">
          Select a user to view and manage their bank connections. You can resync data, trigger Plaid resyncs, or delete
          connections.
        </div>
      </div>

      {/* User Selection */}
      <div className="flex flex-col gap-4">
        <AdminSelectUser
          selectedUserId={selectedUserId}
          onSelectionChange={handleUserSelect}
          className="w-full max-w-md"
        />
      </div>

      {/* Bank Connections Display */}
      {selectedUserId && (
        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">Bank connections for {selectedUserEmail}</div>

          {bankConnectionsLoading && <div className="text-secondary text-sm">Loading bank connections...</div>}

          {!bankConnectionsLoading && bankConnectionsData?.adminGetUserBankConnections.length === 0 && (
            <div className="text-tertiary text-sm">No bank connections found for this user.</div>
          )}

          {!bankConnectionsLoading &&
            bankConnectionsData?.adminGetUserBankConnections &&
            bankConnectionsData.adminGetUserBankConnections.length > 0 && (
              <div className="flex flex-col gap-4">
                {bankConnectionsData.adminGetUserBankConnections.map((bankConnection) => (
                  <div key={bankConnection.id} className="border-primary w-fit rounded-lg border p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {bankConnection.bank.logoUrl && (
                          <img
                            src={bankConnection.bank.logoUrl}
                            alt={bankConnection.bank.name}
                            className="h-10 w-10 rounded border"
                          />
                        )}
                        <div className="flex flex-col">
                          <div className="font-semibold">{bankConnection.bank.name}</div>
                          <div className="text-tertiary text-sm">
                            Last synced: {formatDate(bankConnection.lastSyncedAt)}
                            {bankConnection.lastSyncedAt && ` (${formatTimeAgo(bankConnection.lastSyncedAt)})`}
                          </div>
                          <div className="text-quaternary font-mono text-xs">Plaid ID: {bankConnection.externalId}</div>
                          <div className="text-quaternary font-mono text-xs">Connection ID: {bankConnection.id}</div>
                        </div>
                      </div>
                    </div>

                    {/* Health Status */}
                    {(() => {
                      const healthStatus = healthStatusMap.get(bankConnection.id);
                      if (!healthStatus) {
                        return null;
                      }
                      const getStatusLabel = () => {
                        if (healthStatus.error) {
                          return 'Error';
                        }
                        if (healthStatus.neverSynced) {
                          return 'Never synced';
                        }
                        if (healthStatus.stale) {
                          return 'Stale';
                        }
                        return 'Healthy';
                      };
                      const getStatusPillClasses = () => {
                        if (healthStatus.error) {
                          return 'bg-error-secondary text-error-primary';
                        }
                        if (healthStatus.neverSynced || healthStatus.stale) {
                          return 'bg-warning-secondary text-warning-primary';
                        }
                        return 'bg-success-secondary text-success-primary';
                      };
                      return (
                        <div className="border-primary mt-4 border-t pt-4">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">Health status:</span>
                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusPillClasses()}`}>
                              {getStatusLabel()}
                            </span>
                          </div>
                          {healthStatus.error && (
                            <div className="text-error-primary mt-2 text-sm">{healthStatus.error}</div>
                          )}
                          <button
                            onClick={() => toggleHealthJSONExpanded(bankConnection.id)}
                            className="text-secondary hover:text-primary mt-2 flex items-center gap-1 text-sm"
                          >
                            <span>JSON</span>
                            {expandedHealthJSON.has(bankConnection.id) ? (
                              <ChevronUpIcon className="size-4" />
                            ) : (
                              <ChevronDownIcon className="size-4" />
                            )}
                          </button>
                          {expandedHealthJSON.has(bankConnection.id) && (
                            <pre className="bg-tertiary text-primary mt-2 max-h-64 overflow-auto rounded p-3 font-mono text-xs">
                              {healthStatus.plaidJSON ? JSON.stringify(healthStatus.plaidJSON, null, 2) : 'No data'}
                            </pre>
                          )}
                        </div>
                      );
                    })()}

                    {/* Accounts */}
                    {bankConnection.accounts && bankConnection.accounts.length > 0 && (
                      <div className="border-primary mt-4 border-t pt-4">
                        <button
                          onClick={() => toggleAccountsExpanded(bankConnection.id)}
                          className="text-secondary hover:text-primary mb-2 flex items-center justify-between text-sm font-medium"
                        >
                          <span>Accounts ({bankConnection.accounts.length})</span>
                          {expandedAccounts.has(bankConnection.id) ? (
                            <ChevronUpIcon className="size-4" />
                          ) : (
                            <ChevronDownIcon className="size-4" />
                          )}
                        </button>
                        {expandedAccounts.has(bankConnection.id) && (
                          <div className="flex flex-col gap-2">
                            {bankConnection.accounts.map((account) => (
                              <div key={account.id} className="text-primary bg-secondary rounded px-3 py-2 text-sm">
                                <div className="font-medium">
                                  {account.name} {account.mask && `...${account.mask}`}
                                </div>
                                <div className="text-tertiary text-xs">
                                  {account.type} - {account.subtype}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-2">
                      <Button
                        onClick={() => handleResyncFinancialData(bankConnection.id)}
                        isDisabled={actionLoading === bankConnection.id}
                        color="secondary"
                        size="sm"
                      >
                        {actionLoading === bankConnection.id ? 'Resyncing...' : 'Resync with Plaid'}
                      </Button>
                      <Button
                        onClick={() => handleResyncWithResetCursor(bankConnection.id)}
                        isDisabled={actionLoading === bankConnection.id}
                        color="secondary"
                        size="sm"
                      >
                        {actionLoading === bankConnection.id ? 'Resyncing...' : 'Resync with Plaid (Reset Cursor)'}
                      </Button>
                      <Button
                        onClick={() => handleDeleteClick(bankConnection.id)}
                        isDisabled={actionLoading === bankConnection.id}
                        color="primary-destructive"
                        size="sm"
                      >
                        Delete connection
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen} size="md">
        <Modal.Header onClose={() => setDeleteModalOpen(false)}>Delete bank connection</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-primary flex flex-col gap-3 text-sm">
              <div>Are you sure you want to delete this bank connection? This action cannot be undone.</div>
              <div>The connection will be revoked from Plaid and removed from the database.</div>
              {(() => {
                const bankConnectionToDeleteData = bankConnectionsData?.adminGetUserBankConnections.find(
                  (bc) => bc.id === bankConnectionToDelete
                );
                const userEmail = bankConnectionToDeleteData?.user.email || selectedUserEmail || '';
                return (
                  <div className="text-secondary">
                    <span className="font-medium">{userEmail}</span> will need to login and re-add the bank to restore
                    this information, and some historical data may still be lost.
                  </div>
                );
              })()}
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setDeleteModalOpen(false)} color="secondary" size="sm">
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} isDisabled={!!actionLoading} color="primary-destructive" size="sm">
                {actionLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminBankConnectionsPane;
